// context/UserContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const UserContext = createContext();

const getInitialState = () => {
  try {
    const storedLocation = localStorage.getItem('userLocation');
    if (!storedLocation) return { location: null, postalCode: '' };

    // Guard against plain strings that aren't JSON objects
    const parsed = JSON.parse(storedLocation);
    if (typeof parsed !== 'object' || parsed === null) {
      localStorage.removeItem('userLocation'); // clear the bad value
      return { location: null, postalCode: '' };
    }

    return {
      location: parsed,
      postalCode: parsed.postalCode || '',
    };
  } catch (e) {
    console.error('Failed to parse stored data:', e);
    localStorage.removeItem('userLocation'); // clear so it doesn't keep throwing
    return { location: null, postalCode: '' };
  }
};

export function UserProvider({ children }) {
  const { user: authUser } = useAuth();
  const [userData, setUserData] = useState(getInitialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync with Firebase when authenticated
  useEffect(() => {
    if (!authUser?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, 'users', authUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setUserData(prev => ({
            ...prev,
            uid: docSnapshot.id,
            ...data,
          }));
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching user data:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authUser?.uid]);


  const value = {
    userData,
    loading,
    error,
    getRoles: () => userData?.roles || [],
    getOrganizationId: () => userData?.organizationId || null,
    getOrganizationName: () => userData?.organizationName || '',
    isAuthenticated: !!authUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}